import { Fragment, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { useNavigate, useLocation } from 'react-router-dom';
//constants
import { pagesEnum, timing } from 'constants/ui.constants';
// @mui
import { Stack } from '@mui/system';
// components
import { Page } from 'theme/elements/Page';
import { CenteredNarrowContent } from 'theme/elements/Content';
import CancelDialog from 'components/CancelDialog';
import Stepping from 'theme/elements/Stepper';
// sections
import CreateHeader from 'sections/convert/upsert/CreateHeader';
// reducers
import { useAppSelector, useAppDispatch } from 'hooks/redux.hooks';
import {
  toggleUpserting,
  getSteps,
  getActiveStep,
  intializeConvert,
  setActiveStep,
} from 'state/reducers/convert.reducers';
import {
  cancelDialogProps,
  getHistory,
  getMobile,
  getUi,
  ifPageChange,
  setCheckBeforeNavigate,
  togglePage,
} from 'state/reducers/ui.reducers';
import MobileNavbar from 'sections/layout/MobileNavbar';
import { Convert, convertSteps } from 'constants/convert.constants';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { FormHeader1, HeaderPage1, getUrlBack } from 'theme/elements/Headers';
import { translatedCancelPopup } from 'utils';
import { getConvertById } from 'services/convert.services';
import { FormsStack } from 'theme/elements/Forms';
import { InfoCard } from 'theme/elements/Cards';
import { Paragraph1, Paragraph3 } from 'theme/elements/Paragraphs';
import { format, isValid } from 'date-fns';
import { getBalanceActivityById } from 'services/activity.services';
import { Button, Typography } from '@mui/material';
import { FormButton } from 'theme/elements/Buttons';
import { getFile } from 'services/global.services';
import { getFormattedNumber } from 'utils';

export default function ToppingProfile() {
  const navigate = useNavigate();
  const t = useAppSelector(selectTranslations);
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  //get location
  const paths = location.pathname.split('/');
  const lastPath = paths[paths.length - 1];
  const steps = useAppSelector(getSteps);
  const isMobile = useAppSelector(getMobile);
  const activeStep = useAppSelector(getActiveStep);
  const isLeavingPage = useAppSelector(ifPageChange);
  const ui = useAppSelector(getUi);
  const history = useAppSelector(getHistory);
  const urlback = getUrlBack(location.pathname, history[history.length - 1]);
  const stepProps = {
    handleCancel: () => setOpenCancelDialog(true),
  };
  const dialogProps = useAppSelector(cancelDialogProps);
  const dialogPropsTranslated = useMemo(() => translatedCancelPopup(dialogProps), [dialogProps]);
  const [toppingResult, setToppingResult] = useState<Record<string, any> & Convert | undefined>();
  const prefixFields = 'page.withdrawal.profile';

  const contact = useMemo(() => {
    if (_.has(toppingResult, 'balanceActivity.createdBy')) {
      return _.get(toppingResult, 'balanceActivity.createdBy.firstName') + ' ' + _.get(toppingResult, 'balanceActivity.createdBy.lastName')
    }
    return undefined
  }, [toppingResult])


  const clientBankAccount = useMemo(() => {
    if (toppingResult && _.has(toppingResult, 'companyBankAccounts')) {
      return _.find(toppingResult.companyBankAccounts, { 'id': _.get(toppingResult, 'balanceActivity.bankAccountId') })
    }
    return undefined
  }, [toppingResult])

  const createdByBoUser = useMemo(() => {
    if (_.has(toppingResult, 'balanceActivity.createdByBoUser')) {
      return _.get(toppingResult, 'balanceActivity.createdByBoUser.firstName') + ' ' + _.get(toppingResult, 'balanceActivity.createdByBoUser.lastName')
    }
    return undefined
  }, [toppingResult])

  useEffect(() => {
    dispatch(getBalanceActivityById(lastPath)).then(res => {
      if (res) {
        setToppingResult({
          ...res,
        })
      }
    })
  }, [lastPath]);

  return (
    <Fragment>
      <MobileNavbar
        handleBack={() => {
          dispatch(togglePage(urlback));
        }}
        handleCancel={() => {
          setOpenCancelDialog(true);
        }}
      />
      <Page>
        <Stack spacing={{ sm: 5, xs: 1 }}>
          <CenteredNarrowContent>
            <Fragment>
              {toppingResult && (
                <FormsStack>
                  <InfoCard>
                    <FormHeader1 title={t(`${prefixFields}.title.text`)} />
                    <Paragraph3
                      title={t(prefixFields + '.created_date.text')}
                      text={isValid(new Date(_.get(toppingResult, 'balanceActivity.createdAt') || 0)) ?
                        format(new Date(_.get(toppingResult, 'balanceActivity.createdAt') || 0), timing.FULLDATE) :
                        _.get(toppingResult, 'balanceActivity.createdAt')}
                    />
                    {!_.isNil(_.get(toppingResult, 'balanceActivity.settlementDate')) ? <Paragraph3
                      title={t(prefixFields + '.settlement_date.text')}
                      text={isValid(new Date(_.get(toppingResult, 'balanceActivity.settlementDate') || 0)) ?
                        format(new Date(_.get(toppingResult, 'balanceActivity.settlementDate') || 0), timing.FULLDATE) :
                        _.get(toppingResult, 'balanceActivity.settlementDate')}
                    /> : <></>}
                    <Paragraph3
                      title={t(prefixFields + '.reference.currency')}
                      text={_.get(toppingResult, 'balanceActivity.currency.code')}
                    />
                    <Paragraph3
                      title={t(prefixFields + '.amount.text')}
                      text={_.get(toppingResult, 'balanceActivity.currency.symbol') + '' + getFormattedNumber(_.get(toppingResult, 'balanceActivity.amount'))}
                    />
                    <Paragraph3
                      title={t(prefixFields + '.contact.text')}
                      text={contact}
                    />
                    <Paragraph3
                      title={t(prefixFields + '.bo_contact.text')}
                      text={createdByBoUser}
                    />
                    <Paragraph3
                      title={t(prefixFields + '.status.text')}
                      text={_.get(toppingResult, 'balanceActivity.status')}
                    />
                    <Paragraph3
                      title={t(prefixFields + '.reference.text')}
                      text={_.get(toppingResult, 'balanceActivity.reference')}
                    />

                  </InfoCard>
                  {clientBankAccount &&
                    <InfoCard>
                      <FormHeader1 title={t(`page.withdrawal.profile.bank.title.text`)} />
                      <Stack>
                        <Typography>{_.get(clientBankAccount, 'accountOwnerName')}</Typography>
                        <Typography>{_.get(clientBankAccount, 'bankName')}</Typography>
                        <Typography>{_.get(clientBankAccount, 'bankBranch')}</Typography>
                        <Typography>{_.get(clientBankAccount, 'accountNumber')}</Typography>
                      </Stack>
                    </InfoCard>}
                </FormsStack>
              )}
            </Fragment>
          </CenteredNarrowContent>
        </Stack>
      </Page>
    </Fragment>
  );
}
